import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { Box, Button, Divider, Stack, Tabs, Tab, Typography, Paper } from "@mui/material";
import StepSetup from "./StepSetup";
import StepEpisodes from "./StepEpisodes";
import StepRoles from "./StepRoles";
import StepConstraints from "./StepConstraints";
import { generateSchedule, getPlan } from "../api/postPlansApi";

const STEPS = [
  { key: "setup", label: "Setup" },
  { key: "episodes", label: "Puntate" },
  { key: "roles", label: "Ruoli & Turni" },
  { key: "constraints", label: "Vincoli" },
];

export default function PlanEditor({ startStep = "setup" }) {
  const navigate = useNavigate();
  const { planId: rawId } = useParams();
  const planId = rawId && rawId !== "new" ? rawId : undefined;

  // step corrente
  const startIndex = Math.max(0, STEPS.findIndex(s => s.key === startStep));
  const [activeIdx, setActiveIdx] = useState(startIndex >= 0 ? startIndex : 0);

  // ogni step può registrare una propria save() che il footer chiamerà quando premi "Avanti"
  const saveFnsRef = useRef({});
  const registerSave = useCallback((stepKey, fn) => {
    saveFnsRef.current[stepKey] = fn;               // fn deve ritornare una Promise
  }, []);
  const callSave = useCallback(async (stepKey) => {
    const fn = saveFnsRef.current[stepKey];
    if (!fn) return true;                            // se lo step non ha nulla da salvare, prosegui
    const ok = await fn();                           // convention: fn() ritorna true/false
    return !!ok;
  }, []);

  // carica piano (solo per sicurezza / redirect se manca)
  useEffect(() => {
    let stop = false;
    (async () => {
      if (!planId) return; // su /piani/new non forziamo nulla
      try { await getPlan(planId); } catch { if (!stop) navigate("/piani"); }
    })();
    return () => { stop = true; };
  }, [planId, navigate]);

  const isFirst = activeIdx === 0;
  const isLast  = activeIdx === STEPS.length - 1;

  const goPrev = () => setActiveIdx(i => Math.max(0, i - 1));

  const goNext = async () => {
    // salva lo step corrente (se espone la save)
    const stepKey = STEPS[activeIdx].key;
    const ok = await callSave(stepKey);
    if (!ok) return;

    // se NON è l'ultimo step, vai avanti
    if (!isLast) {
      setActiveIdx(i => Math.min(STEPS.length - 1, i + 1));
      return;
    }

    // se è l’ultimo step (Vincoli) → genera e vai a Timeline
    if (!planId) return; // safety
    try {
      await generateSchedule(planId);
      navigate(`/piani/${planId}/timeline`);
    } catch (e) {
      // se fallisce, prova a mandare comunque a Review
      navigate(`/piani/${planId}/review`);
    }
  };

  const handleTabChange = async (_e, idx) => {
    // se si cambia tab manualmente, proviamo a salvare lo step corrente prima di spostarci
    const curKey = STEPS[activeIdx].key;
    const ok = await callSave(curKey);
    if (!ok) return;
    setActiveIdx(idx);
  };

  return (
    <Box sx={{ pb: 12 }}>
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {planId ? `Piano #${planId}` : "Nuovo Piano di Post"}
          </Typography>
          <Button component={Link} to="/piani" variant="text">Torna all’elenco</Button>
        </Stack>
        <Tabs value={activeIdx} onChange={handleTabChange} sx={{ mt: 1 }}>
          {STEPS.map((s, i) => <Tab key={s.key} label={s.label} />)}
        </Tabs>
      </Paper>

      <Box sx={{ p: 2 }}>
        {STEPS[activeIdx].key === "setup" && (
          <StepSetup planId={planId} registerSave={(fn)=>registerSave("setup", fn)} />
        )}
        {STEPS[activeIdx].key === "episodes" && (
          <StepEpisodes planId={planId} registerSave={(fn)=>registerSave("episodes", fn)} />
        )}
        {STEPS[activeIdx].key === "roles" && (
          <StepRoles planId={planId} registerSave={(fn)=>registerSave("roles", fn)} />
        )}
        {STEPS[activeIdx].key === "constraints" && (
          <StepConstraints planId={planId} registerSave={(fn)=>registerSave("constraints", fn)} />
        )}
      </Box>

      {/* Footer “a prova di scimmia” */}
      <Paper
        elevation={3}
        sx={{
          position: "fixed", left: 0, right: 0, bottom: 0,
          p: 2, borderTop: "1px solid rgba(0,0,0,0.08)", background: "#fff"
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Step {activeIdx + 1} di {STEPS.length}: {STEPS[activeIdx].label}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button onClick={goPrev} disabled={isFirst} variant="outlined">Indietro</Button>
            <Button onClick={goNext} variant="contained">
              {isLast ? "Concludi (genera)" : "Avanti"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
